import { invariant } from '@spotify/confidence-core-react';

import { NormalizedExpression } from '../NormalizedExpression/types';
import {
  ChangeStatus,
  DeltaOperator,
  DeltaValue,
  NormalizedExpressionDelta,
} from './types';

/**
 * Creates a key for an expression to use for matching in unordered collections
 */
function createExpressionKey(expr: NormalizedExpression.Expression): string {
  const parts: string[] = [expr.type];

  switch (expr.type) {
    case 'binary':
      parts.push(expr.attributeName, expr.operator);
      parts.push(NormalizedExpression.valueToString(expr.value));
      break;
    case 'unary':
      parts.push(expr.attributeName, expr.operator);
      break;
    case 'range':
      parts.push(expr.attributeName, expr.operator);
      parts.push(
        NormalizedExpression.valueToString(expr.range.start),
        NormalizedExpression.valueToString(expr.range.end),
        String(expr.range.startInclusive),
        String(expr.range.endInclusive),
      );
      break;
    case 'set': {
      parts.push(expr.attributeName, expr.operator);
      const sortedValues = [...expr.values]
        .map(v => NormalizedExpression.valueToString(v))
        .sort();
      parts.push(...sortedValues);
      break;
    }
    case 'segment':
      parts.push(expr.operator, expr.segmentId);
      break;
    case 'logical': {
      parts.push(expr.operator);
      const operandKeys = expr.operands
        .map(createExpressionKey)
        .sort()
        .join('|');
      parts.push(operandKeys);
      break;
    }
    case 'array':
      parts.push(expr.attributeName, expr.operator);
      parts.push(createExpressionKey({ ...expr.rule, attributeName: '' }));
      break;
    default:
      // Exhaustive check - all expression types are handled
      break;
  }

  return parts.join('::');
}

/**
 * Creates a simpler key for an expression based on type and attribute name only
 * Used for smart matching when exact keys don't match
 */
function createSimpleExpressionKey(
  expr: NormalizedExpression.Expression,
): string | null {
  const parts: string[] = [expr.type];

  switch (expr.type) {
    case 'binary':
    case 'unary':
    case 'range':
    case 'set':
    case 'array':
      parts.push(expr.attributeName);
      return parts.join('::');
    case 'segment':
      // Segment expressions don't have attributeName, use segmentId instead
      parts.push(expr.segmentId);
      return parts.join('::');
    case 'logical':
      // Logical expressions don't have attributeName, can't match by simple key
      return null;
    default:
      // Exhaustive check - all expression types are handled
      return null;
  }
}

/**
 * Diffs two binary expressions
 */
function diffBinary(
  before: NormalizedExpression.BinaryExpression | null,
  after: NormalizedExpression.BinaryExpression | null,
): NormalizedExpressionDelta.BinaryExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    return {
      type: 'binary',
      status: 'added',
      attributeName: after.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
      value: NormalizedExpressionDelta.createUnchangedValue(after.value),
    };
  }

  if (before && !after) {
    return {
      type: 'binary',
      status: 'removed',
      attributeName: before.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
      value: NormalizedExpressionDelta.createUnchangedValue(before.value),
    };
  }

  // Both exist
  const b = before!;
  const a = after!;

  if (b.attributeName !== a.attributeName) {
    // Different attribute names - treat as modified
    return {
      type: 'binary',
      status: 'modified',
      attributeName: a.attributeName,
      operator:
        b.operator !== a.operator
          ? NormalizedExpressionDelta.createModifiedOperator(
              b.operator,
              a.operator,
            )
          : NormalizedExpressionDelta.createUnchangedOperator(a.operator),
      value: NormalizedExpressionDelta.createModifiedValue(b.value, a.value),
    };
  }

  const operatorDelta: DeltaOperator<NormalizedExpression.BinaryOperator> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  const valueDelta: DeltaValue = NormalizedExpressionDelta.valuesEqual(
    b.value,
    a.value,
  )
    ? NormalizedExpressionDelta.createUnchangedValue(a.value)
    : NormalizedExpressionDelta.createModifiedValue(b.value, a.value);

  const status: ChangeStatus =
    operatorDelta.status === 'unchanged' && valueDelta.status === 'unchanged'
      ? 'unchanged'
      : 'modified';

  return {
    type: 'binary',
    status,
    attributeName: a.attributeName,
    operator: operatorDelta,
    value: valueDelta,
  };
}

/**
 * Diffs two unary expressions
 */
function diffUnary(
  before: NormalizedExpression.UnaryExpression | null,
  after: NormalizedExpression.UnaryExpression | null,
): NormalizedExpressionDelta.UnaryExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    return {
      type: 'unary',
      status: 'added',
      attributeName: after.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
    };
  }

  if (before && !after) {
    return {
      type: 'unary',
      status: 'removed',
      attributeName: before.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
    };
  }

  const b = before!;
  const a = after!;

  if (b.attributeName !== a.attributeName) {
    return {
      type: 'unary',
      status: 'modified',
      attributeName: a.attributeName,
      operator:
        b.operator !== a.operator
          ? NormalizedExpressionDelta.createModifiedOperator(
              b.operator,
              a.operator,
            )
          : NormalizedExpressionDelta.createUnchangedOperator(a.operator),
    };
  }

  const operatorDelta: DeltaOperator<NormalizedExpression.UnaryOperator> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  const status: ChangeStatus =
    operatorDelta.status === 'unchanged' ? 'unchanged' : 'modified';

  return {
    type: 'unary',
    status,
    attributeName: a.attributeName,
    operator: operatorDelta,
  };
}

/**
 * Diffs two range expressions
 */
function diffRange(
  before: NormalizedExpression.RangeExpression | null,
  after: NormalizedExpression.RangeExpression | null,
): NormalizedExpressionDelta.RangeExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    return {
      type: 'range',
      status: 'added',
      attributeName: after.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
      range: {
        start: NormalizedExpressionDelta.createUnchangedValue(
          after.range.start,
        ),
        end: NormalizedExpressionDelta.createUnchangedValue(after.range.end),
        startInclusive: NormalizedExpressionDelta.createUnchangedOperator(
          after.range.startInclusive,
        ),
        endInclusive: NormalizedExpressionDelta.createUnchangedOperator(
          after.range.endInclusive,
        ),
      },
    };
  }

  if (before && !after) {
    return {
      type: 'range',
      status: 'removed',
      attributeName: before.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
      range: {
        start: NormalizedExpressionDelta.createUnchangedValue(
          before.range.start,
        ),
        end: NormalizedExpressionDelta.createUnchangedValue(before.range.end),
        startInclusive: NormalizedExpressionDelta.createUnchangedOperator(
          before.range.startInclusive,
        ),
        endInclusive: NormalizedExpressionDelta.createUnchangedOperator(
          before.range.endInclusive,
        ),
      },
    };
  }

  const b = before!;
  const a = after!;

  const operatorDelta: DeltaOperator<NormalizedExpression.RangeOperator> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  const startDelta: DeltaValue = NormalizedExpressionDelta.valuesEqual(
    b.range.start,
    a.range.start,
  )
    ? NormalizedExpressionDelta.createUnchangedValue(a.range.start)
    : NormalizedExpressionDelta.createModifiedValue(
        b.range.start,
        a.range.start,
      );

  const endDelta: DeltaValue = NormalizedExpressionDelta.valuesEqual(
    b.range.end,
    a.range.end,
  )
    ? NormalizedExpressionDelta.createUnchangedValue(a.range.end)
    : NormalizedExpressionDelta.createModifiedValue(b.range.end, a.range.end);

  const startInclusiveDelta: DeltaOperator<boolean> =
    b.range.startInclusive !== a.range.startInclusive
      ? NormalizedExpressionDelta.createModifiedOperator(
          b.range.startInclusive,
          a.range.startInclusive,
        )
      : NormalizedExpressionDelta.createUnchangedOperator(
          a.range.startInclusive,
        );

  const endInclusiveDelta: DeltaOperator<boolean> =
    b.range.endInclusive !== a.range.endInclusive
      ? NormalizedExpressionDelta.createModifiedOperator(
          b.range.endInclusive,
          a.range.endInclusive,
        )
      : NormalizedExpressionDelta.createUnchangedOperator(a.range.endInclusive);

  const hasChanges =
    operatorDelta.status !== 'unchanged' ||
    startDelta.status !== 'unchanged' ||
    endDelta.status !== 'unchanged' ||
    startInclusiveDelta.status !== 'unchanged' ||
    endInclusiveDelta.status !== 'unchanged';

  const status: ChangeStatus = hasChanges ? 'modified' : 'unchanged';

  return {
    type: 'range',
    status,
    attributeName: a.attributeName,
    operator: operatorDelta,
    range: {
      start: startDelta,
      end: endDelta,
      startInclusive: startInclusiveDelta,
      endInclusive: endInclusiveDelta,
    },
  };
}

/**
 * Diffs two set expressions (values are unordered)
 */
function diffSet(
  before: NormalizedExpression.SetExpression | null,
  after: NormalizedExpression.SetExpression | null,
): NormalizedExpressionDelta.SetExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    return {
      type: 'set',
      status: 'added',
      attributeName: after.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
      values: after.values.map(v =>
        NormalizedExpressionDelta.createUnchangedValue(v),
      ),
    };
  }

  if (before && !after) {
    return {
      type: 'set',
      status: 'removed',
      attributeName: before.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
      values: before.values.map(v =>
        NormalizedExpressionDelta.createUnchangedValue(v),
      ),
    };
  }

  const b = before!;
  const a = after!;

  const operatorDelta: DeltaOperator<NormalizedExpression.SetOperator> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  // Build maps for matching values (order doesn't matter)
  const beforeMap = new Map<string, NormalizedExpression.Value[]>();
  for (const val of b.values) {
    const key = NormalizedExpression.valueToString(val);
    if (!beforeMap.has(key)) {
      beforeMap.set(key, []);
    }
    beforeMap.get(key)!.push(val);
  }

  const afterMap = new Map<string, NormalizedExpression.Value[]>();
  for (const val of a.values) {
    const key = NormalizedExpression.valueToString(val);
    if (!afterMap.has(key)) {
      afterMap.set(key, []);
    }
    afterMap.get(key)!.push(val);
  }

  // Process after values in order
  const valueDeltas: DeltaValue[] = [];
  const usedBeforeKeys = new Set<string>();

  for (const afterVal of a.values) {
    const key = NormalizedExpression.valueToString(afterVal);
    const beforeVals = beforeMap.get(key);

    if (beforeVals && beforeVals.length > 0) {
      // Found matching value in before
      const beforeVal = beforeVals.shift()!;
      usedBeforeKeys.add(key);

      if (NormalizedExpressionDelta.valuesEqual(beforeVal, afterVal)) {
        valueDeltas.push(
          NormalizedExpressionDelta.createUnchangedValue(afterVal),
        );
      } else {
        valueDeltas.push(
          NormalizedExpressionDelta.createModifiedValue(beforeVal, afterVal),
        );
      }
    } else {
      // New value
      valueDeltas.push(NormalizedExpressionDelta.createAddedValue(afterVal));
    }
  }

  // Add any remaining before values as removed
  for (const [, vals] of beforeMap.entries()) {
    for (const val of vals) {
      valueDeltas.push(NormalizedExpressionDelta.createRemovedValue(val));
    }
  }

  const hasChanges =
    operatorDelta.status !== 'unchanged' ||
    valueDeltas.some(v => v.status !== 'unchanged');

  const status: ChangeStatus = hasChanges ? 'modified' : 'unchanged';

  return {
    type: 'set',
    status,
    attributeName: a.attributeName,
    operator: operatorDelta,
    values: valueDeltas,
  };
}

/**
 * Diffs two segment expressions
 */
function diffSegment(
  before: NormalizedExpression.SegmentExpression | null,
  after: NormalizedExpression.SegmentExpression | null,
): NormalizedExpressionDelta.SegmentExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    return {
      type: 'segment',
      status: 'added',
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
      segmentId: NormalizedExpressionDelta.createUnchangedValue(
        after.segmentId,
      ),
    };
  }

  if (before && !after) {
    return {
      type: 'segment',
      status: 'removed',
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
      segmentId: NormalizedExpressionDelta.createUnchangedValue(
        before.segmentId,
      ),
    };
  }

  const b = before!;
  const a = after!;

  const operatorDelta: DeltaOperator<NormalizedExpression.SegmentOperator> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  const segmentIdDelta: DeltaValue =
    b.segmentId !== a.segmentId
      ? NormalizedExpressionDelta.createModifiedValue(b.segmentId, a.segmentId)
      : NormalizedExpressionDelta.createUnchangedValue(a.segmentId);

  const status: ChangeStatus =
    operatorDelta.status === 'unchanged' &&
    segmentIdDelta.status === 'unchanged'
      ? 'unchanged'
      : 'modified';

  return {
    type: 'segment',
    status,
    operator: operatorDelta,
    segmentId: segmentIdDelta,
  };
}

/**
 * Diffs two logical expressions (operands are unordered)
 */
function diffLogical(
  before: NormalizedExpression.LogicalExpression | null,
  after: NormalizedExpression.LogicalExpression | null,
): NormalizedExpressionDelta.LogicalExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    return {
      type: 'logical',
      status: 'added',
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
      operands: after.operands.map(op =>
        diffNormalizedExpression(null, op),
      ) as NormalizedExpressionDelta.ExpressionDelta[],
    };
  }

  if (before && !after) {
    return {
      type: 'logical',
      status: 'removed',
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
      operands: before.operands.map(op =>
        diffNormalizedExpression(op, null),
      ) as NormalizedExpressionDelta.ExpressionDelta[],
    };
  }

  const b = before!;
  const a = after!;

  const operatorDelta: DeltaOperator<'and' | 'or'> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  // Match operands by key (order doesn't matter)
  const beforeMap = new Map<string, NormalizedExpression.Expression[]>();
  for (const op of b.operands) {
    const key = createExpressionKey(op);
    if (!beforeMap.has(key)) {
      beforeMap.set(key, []);
    }
    beforeMap.get(key)!.push(op);
  }

  // Also create a map by simple key for smart matching
  const beforeMapBySimpleKey = new Map<
    string,
    NormalizedExpression.Expression[]
  >();
  for (let i = 0; i < b.operands.length; i++) {
    const op = b.operands[i];
    const simpleKey = createSimpleExpressionKey(op);
    if (simpleKey) {
      if (!beforeMapBySimpleKey.has(simpleKey)) {
        beforeMapBySimpleKey.set(simpleKey, []);
      }
      beforeMapBySimpleKey.get(simpleKey)!.push(op);
    }
  }

  const operandDeltas: NormalizedExpressionDelta.ExpressionDelta[] = [];
  const matchedAfterIndices = new Set<number>();
  const usedBeforeIndices = new Set<number>();

  // First pass: Try exact key matching
  for (let i = 0; i < a.operands.length; i++) {
    const afterOp = a.operands[i];
    const key = createExpressionKey(afterOp);
    const beforeOps = beforeMap.get(key);

    if (beforeOps && beforeOps.length > 0) {
      const beforeOp = beforeOps.shift()!;
      const beforeIndex = b.operands.indexOf(beforeOp);
      matchedAfterIndices.add(i);
      usedBeforeIndices.add(beforeIndex);
      const delta = diffNormalizedExpression(beforeOp, afterOp);
      if (delta) {
        operandDeltas.push(delta);
      }
    }
  }

  // Second pass: Smart matching for unmatched after operands
  for (let i = 0; i < a.operands.length; i++) {
    // Skip if already matched in first pass
    if (matchedAfterIndices.has(i)) {
      continue;
    }

    const afterOp = a.operands[i];
    const simpleKey = createSimpleExpressionKey(afterOp);

    if (simpleKey) {
      const beforeOps = beforeMapBySimpleKey.get(simpleKey);

      if (beforeOps && beforeOps.length > 0) {
        // Find the first unused before operand with matching simple key
        let matched = false;
        for (const beforeOp of beforeOps) {
          const beforeIndex = b.operands.indexOf(beforeOp);

          // Skip if already used
          if (usedBeforeIndices.has(beforeIndex)) {
            continue;
          }

          // Try diffing them
          const delta = diffNormalizedExpression(beforeOp, afterOp);
          if (delta && delta.status === 'modified') {
            // It's a modification! Use this match
            matchedAfterIndices.add(i);
            usedBeforeIndices.add(beforeIndex);
            operandDeltas.push(delta);
            matched = true;
            break;
          }
        }

        if (!matched) {
          // No modification found, treat as added
          const delta = diffNormalizedExpression(null, afterOp);
          if (delta) {
            operandDeltas.push(delta);
          }
        }
      } else {
        // No matching simple key, treat as added
        const delta = diffNormalizedExpression(null, afterOp);
        if (delta) {
          operandDeltas.push(delta);
        }
      }
    } else {
      // Can't create simple key (e.g., logical expression), treat as added
      const delta = diffNormalizedExpression(null, afterOp);
      if (delta) {
        operandDeltas.push(delta);
      }
    }
  }

  // Third pass: Add any remaining before operands as removed
  for (let i = 0; i < b.operands.length; i++) {
    if (!usedBeforeIndices.has(i)) {
      const delta = diffNormalizedExpression(b.operands[i], null);
      if (delta) {
        operandDeltas.push(delta);
      }
    }
  }

  const hasChanges =
    operatorDelta.status !== 'unchanged' ||
    operandDeltas.some(op => op.status !== 'unchanged');

  const status: ChangeStatus = hasChanges ? 'modified' : 'unchanged';

  return {
    type: 'logical',
    status,
    operator: operatorDelta,
    operands: operandDeltas,
  };
}

/**
 * Diffs two array expressions
 */
function diffArray(
  before: NormalizedExpression.ArrayExpression | null,
  after: NormalizedExpression.ArrayExpression | null,
): NormalizedExpressionDelta.ArrayExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    const ruleDelta = diffInnerExpression(null, after.rule);
    return {
      type: 'array',
      status: 'added',
      attributeName: after.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        after.operator,
      ),
      rule: ruleDelta!,
    };
  }

  if (before && !after) {
    const ruleDelta = diffInnerExpression(before.rule, null);
    return {
      type: 'array',
      status: 'removed',
      attributeName: before.attributeName,
      operator: NormalizedExpressionDelta.createUnchangedOperator(
        before.operator,
      ),
      rule: ruleDelta!,
    };
  }

  const b = before!;
  const a = after!;

  const operatorDelta: DeltaOperator<NormalizedExpression.ArrayOperator> =
    b.operator !== a.operator
      ? NormalizedExpressionDelta.createModifiedOperator(b.operator, a.operator)
      : NormalizedExpressionDelta.createUnchangedOperator(a.operator);

  const ruleDelta = diffInnerExpression(b.rule, a.rule);

  if (!ruleDelta) {
    return null;
  }

  let ruleHasChanges = false;
  if (ruleDelta.type === 'binary') {
    ruleHasChanges =
      ruleDelta.operator.status !== 'unchanged' ||
      ruleDelta.value.status !== 'unchanged';
  } else if (ruleDelta.type === 'range') {
    ruleHasChanges =
      ruleDelta.operator.status !== 'unchanged' ||
      ruleDelta.range.start.status !== 'unchanged' ||
      ruleDelta.range.end.status !== 'unchanged' ||
      ruleDelta.range.startInclusive.status !== 'unchanged' ||
      ruleDelta.range.endInclusive.status !== 'unchanged';
  } else if (ruleDelta.type === 'set') {
    ruleHasChanges =
      ruleDelta.operator.status !== 'unchanged' ||
      ruleDelta.values.some(v => v.status !== 'unchanged');
  }

  const hasChanges = operatorDelta.status !== 'unchanged' || ruleHasChanges;

  const status: ChangeStatus = hasChanges ? 'modified' : 'unchanged';

  return {
    type: 'array',
    status,
    attributeName: a.attributeName,
    operator: operatorDelta,
    rule: ruleDelta,
  };
}

/**
 * Diffs two inner expressions (for array expressions)
 */
function diffInnerExpression(
  before: NormalizedExpression.InnerExpression | null,
  after: NormalizedExpression.InnerExpression | null,
): NormalizedExpressionDelta.InnerExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    switch (after.type) {
      case 'binary': {
        const fullAfter: NormalizedExpression.BinaryExpression = {
          ...after,
          attributeName: '',
        };
        const delta = diffBinary(null, fullAfter);
        if (!delta) return null;
        const { attributeName, status, ...rest } = delta;
        return rest;
      }
      case 'range': {
        const fullAfter: NormalizedExpression.RangeExpression = {
          ...after,
          attributeName: '',
        };
        const delta = diffRange(null, fullAfter);
        if (!delta) return null;
        const { attributeName, status, ...rest } = delta;
        return rest;
      }
      case 'set': {
        const fullAfter: NormalizedExpression.SetExpression = {
          ...after,
          attributeName: '',
        };
        const delta = diffSet(null, fullAfter);
        if (!delta) return null;
        const { attributeName, status, ...rest } = delta;
        return rest;
      }
      default:
        // Exhaustive check - all inner expression types are handled
        return null;
    }
  }

  if (before && !after) {
    switch (before.type) {
      case 'binary': {
        const fullBefore: NormalizedExpression.BinaryExpression = {
          ...before,
          attributeName: '',
        };
        const delta = diffBinary(fullBefore, null);
        if (!delta) return null;
        const { attributeName, status, ...rest } = delta;
        return rest;
      }
      case 'range': {
        const fullBefore: NormalizedExpression.RangeExpression = {
          ...before,
          attributeName: '',
        };
        const delta = diffRange(fullBefore, null);
        if (!delta) return null;
        const { attributeName, status, ...rest } = delta;
        return rest;
      }
      case 'set': {
        const fullBefore: NormalizedExpression.SetExpression = {
          ...before,
          attributeName: '',
        };
        const delta = diffSet(fullBefore, null);
        if (!delta) return null;
        const { attributeName, status, ...rest } = delta;
        return rest;
      }
      default:
        // Exhaustive check - all inner expression types are handled
        return null;
    }
  }

  // Both exist - types must match
  if (before!.type !== after!.type) {
    // Type mismatch - treat as removed + added
    const added = diffInnerExpression(null, after);
    // Return the added one
    return added;
  }

  const bType = before!.type;
  const aType = after!.type;

  if (bType === 'binary' && aType === 'binary') {
    const fullBefore: NormalizedExpression.BinaryExpression = {
      ...before!,
      attributeName: '',
    };
    const fullAfter: NormalizedExpression.BinaryExpression = {
      ...after!,
      attributeName: '',
    };
    const delta = diffBinary(fullBefore, fullAfter);
    if (!delta) return null;
    const { attributeName, status, ...rest } = delta;
    return rest;
  }
  if (bType === 'range' && aType === 'range') {
    const fullBefore: NormalizedExpression.RangeExpression = {
      ...before!,
      attributeName: '',
    };
    const fullAfter: NormalizedExpression.RangeExpression = {
      ...after!,
      attributeName: '',
    };
    const delta = diffRange(fullBefore, fullAfter);
    if (!delta) return null;
    const { attributeName, status, ...rest } = delta;
    return rest;
  }
  if (bType === 'set' && aType === 'set') {
    const fullBefore: NormalizedExpression.SetExpression = {
      ...before!,
      attributeName: '',
    };
    const fullAfter: NormalizedExpression.SetExpression = {
      ...after!,
      attributeName: '',
    };
    const delta = diffSet(fullBefore, fullAfter);
    if (!delta) return null;
    const { attributeName, status, ...rest } = delta;
    return rest;
  }

  // Should never reach here
  return null;
}

/**
 * Diffs two normalized expressions and returns a delta expression
 * Returns null if there are no changes
 */
export function diffNormalizedExpression(
  before: NormalizedExpression.Expression | null,
  after: NormalizedExpression.Expression | null,
): NormalizedExpressionDelta.ExpressionDelta | null {
  if (!before && !after) {
    return null;
  }

  if (!before && after) {
    // Entire expression added
    switch (after.type) {
      case 'binary':
        return diffBinary(null, after);
      case 'unary':
        return diffUnary(null, after);
      case 'range':
        return diffRange(null, after);
      case 'set':
        return diffSet(null, after);
      case 'segment':
        return diffSegment(null, after);
      case 'logical':
        return diffLogical(null, after);
      case 'array':
        return diffArray(null, after);
      default:
        // Exhaustive check - all expression types are handled
        return null;
    }
  }

  if (before && !after) {
    // Entire expression removed
    switch (before.type) {
      case 'binary':
        return diffBinary(before, null);
      case 'unary':
        return diffUnary(before, null);
      case 'range':
        return diffRange(before, null);
      case 'set':
        return diffSet(before, null);
      case 'segment':
        return diffSegment(before, null);
      case 'logical':
        return diffLogical(before, null);
      case 'array':
        return diffArray(before, null);
      default:
        // Exhaustive check - all expression types are handled
        return null;
    }
  }

  invariant(before, 'before must be defined');
  invariant(after, 'after must be defined');

  // Both exist
  if (before.type !== after.type) {
    // Type mismatch - treat as removed + added (return the added one with modified status)
    const removed = diffNormalizedExpression(before, null);
    const added = diffNormalizedExpression(null, after);
    // For type mismatches, we'll return the added expression but mark it as modified
    // This is a simplification - in practice you might want to show both
    if (added) {
      return { ...added, status: 'modified' };
    }
    return removed;
  }

  // Same type - delegate to specific diff function
  // TypeScript needs explicit type narrowing here
  if (before.type === 'binary' && after.type === 'binary') {
    return diffBinary(before, after);
  }
  if (before.type === 'unary' && after.type === 'unary') {
    return diffUnary(before, after);
  }
  if (before.type === 'range' && after.type === 'range') {
    return diffRange(before, after);
  }
  if (before.type === 'set' && after.type === 'set') {
    return diffSet(before, after);
  }
  if (before.type === 'segment' && after.type === 'segment') {
    return diffSegment(before, after);
  }
  if (before.type === 'logical' && after.type === 'logical') {
    return diffLogical(before, after);
  }
  if (before.type === 'array' && after.type === 'array') {
    return diffArray(before, after);
  }

  // Should never reach here, but TypeScript needs this
  return null;
}
